import * as React from 'react';
import { Avatar, Box, Button, CircularProgress, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import { LocationOn, MyLocation } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MainLoggedInLayout } from '../../components/layouts/main.loggedIn.layout';
import { UserContext } from '../../context/UserContext';
import { getMyEventConversations } from '../../controllers/conversation/conversation';
import { getAccount, getSingleAccount } from '../../controllers/accounts/accounts';
import { updateLocation, updateShareInPersonStatus } from '../../controllers/notify/notifications';
import ConversationModal from '../../components/mingle/ConversationModal';

export const MingleList = () => {
  const navigate = useNavigate();
  const { userDetails } = React.useContext(UserContext);
  const token = userDetails?.token ?? '';
  const myId = userDetails?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['getMyEventConversations', myId, token],
    queryFn: () => getMyEventConversations(token, myId),
    enabled: !!token && !!myId,
  });

  // Whether *my* tracked location may be used to show others an "in person"
  // badge — never sends the location itself anywhere new, just gates the
  // existing comparison server-side.
  const queryClient = useQueryClient();
  const { data: myAccount } = useQuery({
    queryKey: ['getSingleAccount', myId, token],
    queryFn: () => getSingleAccount(myId, token),
    enabled: !!token && !!myId,
  });
  const myLocationCoords = myAccount?.mastodon?.user?.[0]?.location?.coordinates; // GeoJSON [lng, lat]
  const shareInPersonStatus = myAccount?.mastodon?.user?.[0]?.share_in_person_status !== false;
  const shareMutation = useMutation({
    mutationFn: updateShareInPersonStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getSingleAccount', myId, token] });
      // Every place an "in person" badge is shown reads from these — without
      // invalidating them too, the toggle would visibly flip but every badge
      // elsewhere would keep showing stale is_in_person values until those
      // queries happened to refetch on their own.
      queryClient.invalidateQueries({ queryKey: ['getMyEventConversations'] });
      queryClient.invalidateQueries({ queryKey: ['getCheckins'] });
    },
  });
  const toggleShareInPerson = (e) => {
    shareMutation.mutate({ token, mastodon_id: myId, share_in_person_status: e.target.checked });
  };

  const [locating, setLocating] = React.useState(false);
  const [locationError, setLocationError] = React.useState(null);
  const locationMutation = useMutation({
    mutationFn: updateLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getSingleAccount', myId, token] });
      queryClient.invalidateQueries({ queryKey: ['getMyEventConversations'] });
      queryClient.invalidateQueries({ queryKey: ['getCheckins'] });
    },
  });
  const handleUpdateLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Your browser doesn't support location.");
      return;
    }
    setLocating(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        locationMutation.mutate({
          token,
          mastodon_id: myId,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocating(false);
      },
      (error) => {
        console.error('Mingle: failed to get current position', error);
        setLocationError("Couldn't get your location — check your browser's location permission.");
        setLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const [events, setEvents] = React.useState([]);
  const [active, setActive] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const rows = data?.conversations ?? [];
      // Group by event so the page reads as "who's here, per party" rather
      // than one flat list — someone you've partied with at multiple events
      // shows up once per event, not once overall.
      const byEvent = new Map();
      for (const row of rows) {
        if (!byEvent.has(row.event_id)) {
          byEvent.set(row.event_id, { event_id: row.event_id, event_title: row.event_title, event_end_date_time: row.event_end_date_time, rows: [] });
        }
        byEvent.get(row.event_id).rows.push(row);
      }

      const accountCache = new Map();
      const getCachedAccount = (id) => {
        if (!accountCache.has(id)) accountCache.set(id, getAccount(id, token));
        return accountCache.get(id);
      };

      const resolved = await Promise.all(Array.from(byEvent.values()).map(async (group) => {
        const people = await Promise.all(group.rows.map(async (row) => {
          const otherId = row.participants?.find(p => p !== myId);
          if (!otherId) return null;
          const otherAccount = await getCachedAccount(otherId);
          return {
            otherAccount,
            conversations: [row],
            unread: row.unread,
            isInPerson: row.is_in_person,
          };
        }));
        return {
          event_id: group.event_id,
          event_title: group.event_title,
          event_end_date_time: group.event_end_date_time,
          people: people.filter(p => p?.otherAccount?.id),
        };
      }));

      resolved.sort((a, b) => new Date(a.event_end_date_time ?? 0) - new Date(b.event_end_date_time ?? 0));

      if (!cancelled) setEvents(resolved.filter(e => e.people.length > 0));
    };
    if (data) load();
    return () => { cancelled = true };
  }, [data, myId, token]);

  const openPerson = (person) => {
    setActive(person);
    setModalOpen(true);
  };

  const handleStartRealMessage = (account) => {
    navigate('/messages/create', { state: { prefillAccount: account } });
  };

  return (
    <MainLoggedInLayout>
      <Box sx={{
        maxWidth: 780, mx: 'auto', width: '100%',
        minHeight: 'calc(100vh - 180px)',
        display: 'flex', flexDirection: 'column',
      }}>
        <Typography variant="h5" fontWeight={800} sx={{
          mb: 3,
          background: 'linear-gradient(90deg,#E40303,#732982)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          Your Party
        </Typography>

        <Box sx={{ mb: 3, p: 1.5, borderRadius: 2, background: '#F6F3F8' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#732982' }}>
              {Array.isArray(myLocationCoords)
                ? `📍 Location on file: ${myLocationCoords[1].toFixed(4)}, ${myLocationCoords[0].toFixed(4)}`
                : '📍 No location on file yet'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleUpdateLocation}
              disabled={locating || locationMutation.isPending}
              startIcon={locating || locationMutation.isPending ? <CircularProgress size={14} /> : <MyLocation sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700, borderColor: '#732982', color: '#732982' }}
            >
              {locating || locationMutation.isPending ? 'Updating…' : 'Update Location'}
            </Button>
          </Box>
          {locationError && (
            <Typography variant="body2" color="error" sx={{ fontSize: 12, mb: 1 }}>
              {locationError}
            </Typography>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={shareInPersonStatus}
                onChange={toggleShareInPerson}
                disabled={shareMutation.isPending}
                color="secondary"
              />
            }
            label={<Typography sx={{ fontWeight: 700, fontSize: 14 }}>Share in-person check-ins</Typography>}
          />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5, pl: { xs: 0, sm: 6.5 } }}>
            This never shares your exact location — it just shares which events you're in person at.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {isLoading ? null : events.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{
                maxWidth: 400, mx: 'auto', textAlign: 'center',
                p: 4, borderRadius: 3,
                background: 'linear-gradient(120deg,#f3f0ff,#fff0f6)',
                border: '1px solid rgba(115,41,130,0.15)',
              }}>
                <Box sx={{ fontSize: 48, mb: 1.5 }}>🎉</Box>
                <Typography fontWeight={800} fontSize={18} gutterBottom>No one to party with yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Check in to events to get your party going. People who want to party with you — and who you want to party with — will show up here.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
              gap: 2,
            }}>
              {events.map((event) => (
                <Box key={event.event_id} sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  p: 1.5, borderRadius: 3, background: '#F8F6FA',
                }}>
                  <Typography fontWeight={800} fontSize={13} color="#732982" textAlign="center" sx={{ lineHeight: 1.35 }}>
                    {event.event_title}
                    {event.people.some(p => p.unread) && (
                      <Box component="span" sx={{ color: '#E40303', fontWeight: 900 }}> •</Box>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 1.25 }}>
                    {event.people.map((person) => (
                      <Box
                        key={person.otherAccount.id}
                        onClick={() => openPerson(person)}
                        sx={{ position: 'relative', cursor: 'pointer' }}
                      >
                        <Avatar
                          src={person.otherAccount?.avatar}
                          sx={{
                            width: 44, height: 44,
                            border: person.isInPerson ? '2.5px solid #008026' : '2.5px solid #fff',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                          }}
                        />
                        {person.unread && (
                          <Box sx={{
                            position: 'absolute', top: 0, right: 0, width: 12, height: 12, borderRadius: '50%',
                            background: '#E40303', border: '2px solid #fff',
                          }} />
                        )}
                        {person.isInPerson && (
                          <Tooltip title="Checked in nearby the event — they're here in person">
                            <Box sx={{
                              position: 'absolute', bottom: -2, right: -2, width: 16, height: 16, borderRadius: '50%',
                              background: '#008026', border: '2px solid #fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <LocationOn sx={{ fontSize: 10, color: '#fff' }} />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {active && (
          <ConversationModal
            open={modalOpen}
            setOpen={setModalOpen}
            person={active}
            onStartRealMessage={handleStartRealMessage}
          />
        )}
      </Box>
    </MainLoggedInLayout>
  );
};
